import { isEmptyOrNull } from "@ifx-react/ifx-react-core";
import { isEmpty } from "@ifx-react/ifx-react-core/node_modules/ramda";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useSelector } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import { IFXLoader } from "../../../../components/common/IFXLoader";
import {
  useCarrierProperties,
  useIsCarrierPropertiesLoaded,
} from "../../../../hooks/common/useCarrierProperty";
import { authorizeConfigRecursive } from "../../../../hooks/portal/userSecurityHooks";
import { navigateIframe } from "../../../ASM/IframeNavigation";
import { IFXMenuItem } from "./IFXMenuItem";
import { useIFXMenus } from "./NavItems";

const MenuList = React.memo(
  ({
    selectedMenu: { menu = [] } = {},
    setHideOnMouseLeave,
    setIconClicked,
  } = {}) => {
    const onClick = useCallback(() => {
      setHideOnMouseLeave(true);
      setIconClicked(null);
    }, []);

    const customMenuCPList = useMemo(
      () =>
        menu.reduce((out, item) => {
          if (item?.customLink?.carrierPropertyKey) {
            out.push(item.customLink.carrierPropertyKey);
          }
          if (item?.content?.length) {
            item.content.forEach(childMenuItem => {
              if (childMenuItem?.customLink?.carrierPropertyKey) {
                out.push(childMenuItem.customLink.carrierPropertyKey);
              }
            });
          }
          return out;
        }, []),
      [menu]
    );

    const cpList = useCarrierProperties(customMenuCPList);
    const isCPloaded = useIsCarrierPropertiesLoaded(customMenuCPList);

    const menuList = useMemo(
      () =>
        menu.map((menuItem, idx) => {
          const { content } = menuItem;

          return (
            <Fragment key={`ifx-menu-${idx}`}>
              {!content?.length ? (
                <HeaderItemLink
                  {...menuItem}
                  onClick={onClick}
                  cpList={cpList}
                />
              ) : (
                <>
                  <HeaderItemLabel {...menuItem} />
                  {content?.map((childMenuItem, childIdx) => (
                    <ChildItemLink
                      key={`ifx-sub-menu-${childIdx}`}
                      {...childMenuItem}
                      onClick={onClick}
                      cpList={cpList}
                    />
                  ))}
                </>
              )}
            </Fragment>
          );
        }),
      [menu, isCPloaded, cpList]
    );

    return (
      <div className="pl-2">
        {!isCPloaded ? <IFXLoader enable /> : <ul>{menuList}</ul>}
      </div>
    );
  }
);

export const IFXVerticalMenu = React.memo(
  ({
    showRightMenu,
    setHideOnMouseLeave,
    sideMenuScrollBarRef,
    height,
    iconClicked,
    setIconClicked,
  }) => {
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [selectedMenu, setSelectedMenu] = useState({});
    const [hideMenu, setHideMenu] = useState(false);

    const authTaskList = useSelector(
      state => state.PortalReducer.UserContextReducer.userInfo.taskList
    );

    const IFXMenus = useIFXMenus();

    const tmpMenus = useMemo(() => {
      return (
        (authTaskList &&
          authTaskList.length > 0 &&
          IFXMenus.map((mConfig, i) => {
            const newmConfig = authorizeConfigRecursive(
              mConfig,
              {},
              authTaskList
            );
            if (newmConfig && newmConfig.dashboard)
              return (
                <IFXMenuItem
                  key={`${mConfig?.dashboard?.title || "ifx-menu-icon-" + i}`}
                  id={`ifx-menu-icon-${i}`}
                  config={newmConfig}
                  source={mConfig?.dashboard?.source || ""}
                  title={mConfig?.dashboard?.title || ""}
                  setSelectedMenu={setSelectedMenu}
                  selectedMenu={selectedMenu}
                  showRightMenu={showRightMenu}
                  setHideOnMouseLeave={setHideOnMouseLeave}
                  scrollbarRef={sideMenuScrollBarRef}
                  setRedirectUrl={setRedirectUrl}
                  {...{ iconClicked, setIconClicked }}
                  //checked={iconClicked === newmConfig?.dashboard?.title}
                />
              );
          })) ||
        []
      );
    }, [IFXMenus, authTaskList, iconClicked]);

    const _showRightMenu = useMemo(() => {
      const show = !isEmpty(selectedMenu) && !showRightMenu;
      show && setHideMenu(false);
      return show;
    }, [selectedMenu, showRightMenu]);

    useEffect(() => {
      if (!_showRightMenu) {
        const timer = setTimeout(() => {
          setHideMenu(true); //unmount menu with delay of 500
        }, 500);
        return () => clearTimeout(timer);
      } else {
        const timer = setTimeout(() => {
          sideMenuScrollBarRef.current.updateScroll();
        });
        return () => clearTimeout(timer);
      }
    }, [_showRightMenu, sideMenuScrollBarRef]);

    return (
      <>
        {IFXMenus?.length ? (
          <>
            {!isEmptyOrNull(redirectUrl) && <Redirect to={redirectUrl} />}
            <div className="app-sidebar-menu-container app-sidebar-menu-left">
              <div>{tmpMenus}</div>
            </div>

            <div
              className={`app-sidebar-menu-container app-sidebar-menu-right${(_showRightMenu &&
                " active") ||
                ""}`}
              style={{ height }}
            >
              <div className="py-2 pl-2 pr-1">
                {!hideMenu && (
                  <PerfectScrollbar ref={sideMenuScrollBarRef}>
                    <MenuList
                      selectedMenu={selectedMenu}
                      setHideOnMouseLeave={setHideOnMouseLeave}
                      setIconClicked={setIconClicked}
                    />
                  </PerfectScrollbar>
                )}
              </div>
            </div>
          </>
        ) : (
          <IFXLoader enable />
        )}
      </>
    );
  }
);

const HeaderItemLink = props => (
  <MenuLink className="ifx-menu-style" {...props} />
);

const HeaderItemLabel = ({ label }) => (
  <li className="ifx-menu-with-content-style">{label}</li>
);

const ChildItemLink = props => (
  <MenuLink className="pl-4 ifx-submenu-style" {...props} />
);

const MenuLink = ({
  className,
  to = "/",
  label,
  onClick,
  customLink,
  cpList,
  childPath,
}) => {
  const { carrierPropertyKey, linkProps } = customLink || {};

  const cpValue = !!carrierPropertyKey && cpList[customLink.carrierPropertyKey];

  const _onClick = useCallback(() => {
    onClick();
    if (childPath) {
      navigateIframe(childPath);
    }
  }, [childPath]);

  return (
    <li className={className} onClick={_onClick}>
      <Link
        to={cpValue ? { pathname: cpValue } : to}
        {...(linkProps ? linkProps : {})}
      >
        {label}
      </Link>
    </li>
  );
};
